import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Target, Save, X, Plus, ArrowLeft, RefreshCw, Zap } from 'lucide-react';
import { Card, Button, Badge, Input, Label } from '../../../components/ui';
import { useToastActions } from '../../../components/ui';
import { ResumeSwitcher } from '../../../components/ResumeSwitcher';
import { useResumeContext } from '../../../hooks/useResumeContext';
import { apiFetch } from '../../../lib/api-client';
import type { UserObjective, ObjectiveType } from '../../../types';
import { OBJECTIVE_DEFINITIONS } from '../../../types';

// Default objective structure
const defaultObjective: UserObjective = {
  objective: 'EASY_WIN' as ObjectiveType,
  constraints: {},
};

export function ObjectivesPage() {
  const { } = useResumeContext();
  const navigate = useNavigate();
  const { success, error } = useToastActions();
  
  // State for objective data
  const [objective, setObjective] = useState<UserObjective>(defaultObjective);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  
  const [isDirty, setIsDirty] = useState(false);
  const [currentObjective, setCurrentObjective] = useState<ObjectiveType>(objective.objective);
  const [explorationRate, setExplorationRate] = useState<number>((objective.constraints.exploration_rate || 0.15) * 100);
  const [locations, setLocations] = useState<string[]>(objective.constraints.locations || []);
  const [newLocation, setNewLocation] = useState('');
  const [blocklist, setBlocklist] = useState<string[]>(objective.constraints.blocklist || []);
  const [newBlocklistItem, setNewBlocklistItem] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Load objectives when component mounts
  useEffect(() => {
    loadObjectives();
  }, []);

  // Update form state when objective data changes
  useEffect(() => {
    setCurrentObjective(objective.objective);
    setExplorationRate((objective.constraints.exploration_rate || 0.15) * 100);
    setLocations(objective.constraints.locations || []);
    setBlocklist(objective.constraints.blocklist || []);
  }, [objective]);

  const loadObjectives = async () => {
    setLoading(true);
    setLoadError(null);
    
    try {
      const response = await apiFetch('/objectives/users/me');
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setObjective(data.data || defaultObjective);
        } else {
          throw new Error(data.error?.message || 'Failed to load objectives');
        }
      } else if (response.status === 404) {
        // No objectives set yet, use default
        setObjective(defaultObjective);
      } else {
        throw new Error(`Failed to load objectives: ${response.status}`);
      }
    } catch (e: any) {
      console.error('Failed to load objectives:', e);
      setLoadError(e?.message || 'Failed to load objectives');
      setObjective(defaultObjective); // Fallback to default
    } finally {
      setLoading(false);
    }
  };

  const addLocation = () => {
    if (newLocation.trim() && !locations.includes(newLocation.trim())) {
      setLocations([...locations, newLocation.trim()]);
      setNewLocation('');
      setIsDirty(true);
    }
  };

  const removeLocation = (location: string) => {
    setLocations(locations.filter(l => l !== location));
    setIsDirty(true);
  };

  const addBlocklistItem = () => {
    if (newBlocklistItem.trim() && !blocklist.includes(newBlocklistItem.trim())) {
      setBlocklist([...blocklist, newBlocklistItem.trim()]);
      setNewBlocklistItem('');
      setIsDirty(true);
    }
  };

  const removeBlocklistItem = (item: string) => {
    setBlocklist(blocklist.filter(b => b !== item));
    setIsDirty(true);
  };

  const handleSaveClick = (e: React.FormEvent) => {
    e.preventDefault();
    setShowConfirmation(true);
  };

  const handleConfirmSave = async () => {
    setShowConfirmation(false);
    setSaveLoading(true);
    
    try {
      // Build the objective data structure
      const objectiveData: UserObjective = {
        objective: currentObjective,
        constraints: {
          locations: locations.length > 0 ? locations : undefined,
          remote: document.querySelector<HTMLInputElement>('input[name="remote"]')?.checked || false,
          salary_floor: (() => {
            const salaryInput = document.querySelector<HTMLInputElement>('input[name="salary_floor"]');
            return salaryInput?.value ? parseInt(salaryInput.value) : undefined;
          })(),
          seniority_levels: (() => {
            const checkboxes = document.querySelectorAll<HTMLInputElement>('input[name="seniority_levels"]:checked');
            const values = Array.from(checkboxes).map(cb => cb.value).filter(Boolean);
            return values.length > 0 ? values : undefined;
          })(),
          employment_types: (() => {
            const checkboxes = document.querySelectorAll<HTMLInputElement>('input[name="employment_types"]:checked');
            const values = Array.from(checkboxes).map(cb => cb.value).filter(Boolean);
            return values.length > 0 ? values : undefined;
          })(),
          exploration_rate: explorationRate / 100,
          small_company_bias: document.querySelector<HTMLInputElement>('input[name="small_company_bias"]')?.checked || false,
          blocklist: blocklist.length > 0 ? blocklist : undefined,
        },
      };

      const response = await apiFetch('/objectives/users/me', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(objectiveData),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          success('Objectives Updated', 'Your job search strategy has been updated.');
          setIsDirty(false);
          setObjective(objectiveData); // Update local state
        } else {
          throw new Error(data.error?.message || 'Failed to update objectives');
        }
      } else {
        throw new Error(`Failed to update objectives: ${response.status}`);
      }
    } catch (e: any) {
      console.error('Failed to update objectives:', e);
      error('Update Failed', e?.message || 'Failed to update objectives');
    } finally {
      setSaveLoading(false);
    }
  };

  // Live preview computation
  const getPreviewText = () => {
    const objectiveDef = OBJECTIVE_DEFINITIONS[currentObjective];
    const parts = [
      `Focus: ${objectiveDef.name}`,
      locations.length > 0 ? `Locations: ${locations.join(', ')}` : null,
      explorationRate > 15 ? `High exploration (${explorationRate}%)` : null,
      blocklist.length > 0 ? `Avoiding ${blocklist.length} companies` : null,
    ].filter(Boolean);
    
    return parts.join(' • ');
  };

  if (loading && !objective) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2">
            <RefreshCw className="h-4 w-4 animate-spin" />
            <span className="text-muted-foreground">Loading objectives...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/strategy')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Target className="h-6 w-6 text-primary" />
            <h1 className="text-3xl font-bold text-foreground">Objectives & Constraints</h1>
          </div>
          <p className="text-muted-foreground ml-11">
            Configure your job search targeting and filtering preferences.
          </p>
        </div>
        <ResumeSwitcher className="w-64" />
      </div>

      {/* Error display */}
      {loadError && (
        <Card>
          <div className="text-center py-8 bg-destructive/10 rounded-2xl">
            <div className="text-destructive mb-2">Error loading objectives</div>
            <p className="text-muted-foreground mb-4">{loadError}</p>
            <Button onClick={loadObjectives} variant="outline">
              Try Again
            </Button>
          </div>
        </Card>
      )}

      {/* Live Preview */}
      <Card>
        <div className="bg-muted/30 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="h-4 w-4 text-primary" />
            <h3 className="font-medium text-foreground">Live Preview</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            {getPreviewText()}
          </p>
          <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
            <span>Exploration: {explorationRate}%</span>
            <span>•</span>
            <span>Strategy weights will be recalculated</span>
          </div>
        </div>
      </Card>

      <form className="space-y-6" onSubmit={handleSaveClick}>
        {/* Primary Objective */}
        <Card>
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Primary Objective</h2>
              <p className="text-muted-foreground text-sm mt-1">
                Choose your main job search focus. This affects strategy weights and targeting.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(Object.entries(OBJECTIVE_DEFINITIONS) as [ObjectiveType, typeof OBJECTIVE_DEFINITIONS[ObjectiveType]][]).map(([key, def]) => (
                <label key={key} className="relative cursor-pointer">
                  <input 
                    type="radio" 
                    name="objective" 
                    value={key}
                    checked={currentObjective === key}
                    onChange={(e) => {
                      setCurrentObjective(e.target.value as ObjectiveType);
                      setIsDirty(true);
                    }}
                    className="sr-only peer"
                  />
                  <div className="p-4 rounded-2xl border-2 border-border transition-all peer-checked:border-primary peer-checked:bg-primary/5 hover:bg-muted/50">
                    <div className="flex items-center gap-3">
                      <div className={`h-3 w-3 rounded-full ${def.color}`} />
                      <div>
                        <h3 className="font-medium text-foreground">{def.name}</h3>
                        <p className="text-sm text-muted-foreground">{def.description}</p>
                      </div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </Card>

        {/* Location Constraints */}
        <Card>
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Location Preferences</h2>
              <p className="text-muted-foreground text-sm mt-1">
                Specify preferred locations or leave empty for no location restrictions.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex gap-2">
                <Input
                  value={newLocation}
                  onChange={(e) => setNewLocation(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addLocation())}
                  placeholder="e.g., San Francisco, CA"
                  className="flex-1"
                />
                <Button type="button" variant="secondary" onClick={addLocation}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {locations.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {locations.map((location) => (
                    <Badge key={location} variant="secondary" className="gap-1">
                      📍 {location}
                      <button
                        type="button"
                        onClick={() => removeLocation(location)}
                        className="hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {/* Hidden inputs for form submission */}
              {locations.map((location) => (
                <input key={location} type="hidden" name="locations" value={location} />
              ))}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="remote"
                name="remote"
                value="true"
                defaultChecked={objective.constraints.remote}
                className="rounded border-border text-primary focus:ring-primary/50"
              />
              <Label htmlFor="remote">Remote work only</Label>
            </div>
          </div>
        </Card>

        {/* Compensation & Seniority */}
        <Card>
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Compensation & Level</h2>
              <p className="text-muted-foreground text-sm mt-1">
                Set minimum salary and preferred seniority levels.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="salary_floor">Minimum Salary (USD)</Label>
                <Input
                  id="salary_floor"
                  name="salary_floor"
                  type="number"
                  placeholder="e.g., 80000"
                  defaultValue={objective.constraints.salary_floor}
                />
              </div>

              <div>
                <Label>Seniority Levels</Label>
                <div className="space-y-2 mt-2">
                  {['Entry', 'Mid', 'Senior', 'Staff', 'Principal', 'Director', 'VP', 'C-Level'].map((level) => (
                    <label key={level} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        name="seniority_levels"
                        value={level.toLowerCase()}
                        defaultChecked={objective.constraints.seniority_levels?.includes(level.toLowerCase())}
                        className="rounded border-border text-primary focus:ring-primary/50"
                      />
                      <span className="text-sm">{level}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Advanced Options */}
        <Card>
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Advanced Options</h2>
              <p className="text-muted-foreground text-sm mt-1">
                Fine-tune your search strategy and filtering.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="exploration_rate">Exploration Rate (%)</Label>
                <Input
                  id="exploration_rate"
                  name="exploration_rate"
                  type="range"
                  min="0"
                  max="50"
                  step="5"
                  value={explorationRate}
                  onChange={(e) => {
                    setExplorationRate(Number(e.target.value));
                    setIsDirty(true);
                  }}
                  className="mt-2"
                />
                <div className="text-center text-sm font-medium text-foreground mt-1">
                  {explorationRate}%
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  Higher values show more diverse, experimental job matches.
                </div>
              </div>

              <div>
                <Label>Employment Types</Label>
                <div className="space-y-2 mt-2">
                  {['Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship'].map((type) => (
                    <label key={type} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        name="employment_types"
                        value={type.toLowerCase()}
                        defaultChecked={objective.constraints.employment_types?.includes(type.toLowerCase())}
                        className="rounded border-border text-primary focus:ring-primary/50"
                      />
                      <span className="text-sm">{type}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="small_company_bias"
                name="small_company_bias"
                value="true"
                defaultChecked={objective.constraints.small_company_bias}
                className="rounded border-border text-primary focus:ring-primary/50"
              />
              <Label htmlFor="small_company_bias">Prefer startups and small companies</Label>
            </div>
          </div>
        </Card>

        {/* Company Blocklist */}
        <Card>
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Company Blocklist</h2>
              <p className="text-muted-foreground text-sm mt-1">
                Companies or keywords to avoid in job recommendations.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex gap-2">
                <Input
                  value={newBlocklistItem}
                  onChange={(e) => setNewBlocklistItem(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addBlocklistItem())}
                  placeholder="e.g., Facebook, consulting"
                  className="flex-1"
                />
                <Button type="button" variant="secondary" onClick={addBlocklistItem}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {blocklist.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {blocklist.map((item) => (
                    <Badge key={item} variant="secondary" className="gap-1">
                      🚫 {item}
                      <button
                        type="button"
                        onClick={() => removeBlocklistItem(item)}
                        className="hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {/* Hidden inputs for form submission */}
              {blocklist.map((item) => (
                <input key={item} type="hidden" name="blocklist" value={item} />
              ))}
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-between sticky bottom-4 bg-background/95 backdrop-blur-sm border border-border rounded-2xl p-4 shadow-lg">
          <Button variant="ghost" onClick={() => navigate('/strategy')}>
            Cancel
          </Button>
          
          <div className="flex items-center gap-3">
            {isDirty && (
              <div className="text-sm text-muted-foreground">
                You have unsaved changes
              </div>
            )}
            <Button type="submit" disabled={!isDirty || saveLoading} className="gap-2">
              <Save className={`h-4 w-4 ${saveLoading ? 'animate-spin' : ''}`} />
              {saveLoading ? 'Saving...' : 'Save & Apply to Feed'}
            </Button>
          </div>
        </div>
      </form>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <Card className="max-w-md w-full mx-4">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Apply Changes to Feed?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  This will update your job search strategy and regenerate recommendations.
                </p>
              </div>

              <div className="bg-muted/30 rounded-xl p-3">
                <h4 className="font-medium text-foreground mb-2">Preview Changes:</h4>
                <div className="text-sm text-muted-foreground space-y-1">
                  <div>• Objective: {OBJECTIVE_DEFINITIONS[currentObjective].name}</div>
                  <div>• Exploration: {explorationRate}%</div>
                  {locations.length > 0 && <div>• Locations: {locations.slice(0, 2).join(', ')}{locations.length > 2 ? ` +${locations.length - 2} more` : ''}</div>}
                  {blocklist.length > 0 && <div>• Blocklist: {blocklist.length} companies</div>}
                </div>
              </div>

              <div className="text-xs text-muted-foreground bg-info/10 border border-info/20 rounded-xl p-3">
                <p>
                  <strong>Note:</strong> Strategy weights will be recalculated and new recommendations 
                  will appear in your feed within a few minutes.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowConfirmation(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmSave}
                  className="flex-1 gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Apply Changes
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}